import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email,   setEmail]   = useState('');
  const [password, setPassword] = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      // Look up this email via RPC (doesn't leak full customer data)
      const { data: lookupRows } = await supabase.rpc('customer_lookup_for_reset', {
        p_email: email.trim().toLowerCase(),
      });
      const existing = (Array.isArray(lookupRows) && lookupRows[0]) || lookupRows;
      if (!existing || !existing.id) {
        throw new Error('No account found with this email. Please check and try again.');
      }

      // Insert a password reset request for admin to handle
      const { error: insertErr } = await supabase.from('password_resets').insert({
        email: email.trim().toLowerCase(),
        customer_name: existing.name || 'Unknown',
        customer_id: existing.id,
        status: 'pending',
      });
      // If table doesn't exist or insert fails, still show success (so we don't leak info)
      if (insertErr) console.warn('Reset request insert:', insertErr.message);

      setResetSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: '#0f172a', border: '1.5px solid #334155',
    borderRadius: 10, color: '#fff', fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#94a3b8', marginBottom: 6,
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e293b)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <div style={{
        background: '#1e293b', borderRadius: 20, padding: '48px 40px',
        width: '100%', maxWidth: 400, boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
        border: '1px solid #334155',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>{'\uD83D\uDEE1\uFE0F'}</div>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 4px' }}>
            ParentsProtection
          </h1>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
            {resetMode
              ? (resetSent ? 'Request submitted!' : 'Reset your password')
              : 'Sign in to your parent dashboard'}
          </p>
        </div>

        {resetMode && resetSent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>{'\u2705'}</div>
            <h3 style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              Password Reset Requested
            </h3>
            <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
              Your request has been sent for <strong style={{ color: '#e2e8f0' }}>{email}</strong>.
            </p>
            <div style={{
              background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
              padding: '16px 20px', marginBottom: 24, textAlign: 'left',
            }}>
              <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 8 }}>
                What happens next?
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                1. Our admin team will review your request<br/>
                2. We'll reset your password within 24 hours<br/>
                3. You'll receive your new password via email or WhatsApp
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 20, lineHeight: 1.5 }}>
              Need it faster? Contact us on WhatsApp for instant support.
            </p>
            <button
              onClick={() => { setResetMode(false); setResetSent(false); setError(''); }}
              style={{
                width: '100%', padding: '14px',
                background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Back to Login
            </button>
          </div>
        ) : resetMode ? (
          <form onSubmit={handleReset}>
            {error && (
              <div style={{
                background: '#fee2e2', color: '#dc2626', padding: '10px 14px',
                borderRadius: 10, fontSize: 13, marginBottom: 16, textAlign: 'center',
              }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Email Address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Enter your registered email" style={inputStyle} />
              <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>
                Enter the email you used to sign up. Our admin will reset your password.
              </div>
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '14px',
              background: loading ? '#334155' : 'linear-gradient(135deg, #0ea5e9, #2563eb)',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 15, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}>
              {loading ? 'Submitting...' : 'Request Password Reset'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#64748b' }}>
              <span
                onClick={() => { setResetMode(false); setError(''); }}
                style={{ color: '#0ea5e9', cursor: 'pointer', fontWeight: 600 }}
              >
                Back to Login
              </span>
            </p>
          </form>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  background: '#fee2e2', color: '#dc2626', padding: '10px 14px',
                  borderRadius: 10, fontSize: 13, marginBottom: 16, textAlign: 'center',
                }}>
                  {error}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="parent@email.com" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Your password" style={inputStyle} />
              </div>
              <div style={{ textAlign: 'right', marginBottom: 20 }}>
                <span
                  onClick={() => { setResetMode(true); setError(''); }}
                  style={{ fontSize: 12, color: '#0ea5e9', cursor: 'pointer', fontWeight: 600 }}
                >
                  Forgot password?
                </span>
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '14px',
                background: loading ? '#334155' : 'linear-gradient(135deg, #0ea5e9, #2563eb)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
                fontFamily: 'inherit',
              }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#64748b' }}>
              Don't have an account?{' '}
              <Link to="/signup" style={{ color: '#0ea5e9', textDecoration: 'none', fontWeight: 600 }}>
                Sign up free
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
